/**
 * Defers the decorative background video until it is near the viewport.
 *
 * The video sits ~1200px below the fold, but `autoplay` makes the browser fetch and buffer
 * it during the initial load, about 4 MB before the visitor has seen anything. `preload`
 * does not help here: autoplay overrides it. So the source is held in `data-src` and only
 * attached once the element is close to being scrolled into view.
 *
 * A poster still paints in the meantime, so nothing jumps when the video takes over.
 */
export function setupLazyMedia(root: HTMLElement) {
  const videos = Array.from(
    root.querySelectorAll<HTMLVideoElement>('video[data-lazy-src]')
  );
  const images = Array.from(
    root.querySelectorAll<HTMLImageElement>('img[data-lazy-src]')
  );
  const backgrounds = Array.from(
    root.querySelectorAll<HTMLElement>('[data-lazy-background]')
  );
  const targets: Element[] = [...videos, ...images, ...backgrounds];
  if (!targets.length) return () => {};

  const load = (video: HTMLVideoElement) => {
    const src = video.dataset.lazySrc;
    if (!src || video.dataset.lazyLoaded === 'true') return;
    video.dataset.lazyLoaded = 'true';
    const poster = video.dataset.lazyPoster;
    if (poster) video.poster = poster;

    const source = document.createElement('source');
    source.src = src;
    source.type = video.dataset.lazyType ?? 'video/mp4';
    video.append(source);
    video.load();
    // autoplay is muted, so this resolves; a rejection just leaves the poster showing.
    void video.play().catch(() => {});
  };

  const loadImage = (image: HTMLImageElement) => {
    const src = image.dataset.lazySrc;
    if (!src || image.dataset.lazyLoaded === 'true') return;
    image.dataset.lazyLoaded = 'true';
    image.src = src;
  };

  const loadBackground = (element: HTMLElement) => {
    const background = element.dataset.lazyBackground;
    if (!background || element.dataset.lazyLoaded === 'true') return;
    element.dataset.lazyLoaded = 'true';
    const property = element.dataset.lazyBackgroundProperty;
    if (property) element.style.setProperty(property, background);
    else element.style.backgroundImage = background;
  };

  const loadTarget = (target: Element) => {
    if (target instanceof HTMLVideoElement) load(target);
    else if (target instanceof HTMLImageElement) loadImage(target);
    else loadBackground(target as HTMLElement);
  };

  if (!('IntersectionObserver' in window)) {
    targets.forEach(loadTarget);
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        loadTarget(entry.target);
        observer.unobserve(entry.target);
      }
    },
    // A short lead keeps below-the-fold media off the initial critical path while
    // still fetching it before the visitor reaches the section.
    { rootMargin: '160px 0px' }
  );

  targets.forEach((target) => observer.observe(target));

  return () => observer.disconnect();
}
